
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Users, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TeamData {
  id: string;
  name: string;
  image_url?: string;
  guardsCount?: number;
}

interface TeamStatCardProps {
  team: TeamData;
  isSelected: boolean;
  onClick: (teamId: string) => void;
}

const TeamStatCard: React.FC<TeamStatCardProps> = ({ 
  team, 
  isSelected,
  onClick
}) => {
  // Generate subtle variations based on team ID for visual diversity
  const teamVariation = team.id.charCodeAt(0) % 6;
  
  const getGradientVariation = () => {
    switch (teamVariation) {
      case 0:
        return "from-blue-50/80 to-blue-100/60 dark:from-blue-800/90 dark:to-blue-900/80"; // Soft blue
      case 1:
        return "from-purple-50/80 to-purple-100/60 dark:from-purple-800/90 dark:to-purple-900/80"; // Soft purple
      case 2:
        return "from-emerald-50/80 to-emerald-100/60 dark:from-emerald-800/90 dark:to-emerald-900/80"; // Soft emerald
      case 3:
        return "from-orange-50/80 to-orange-100/60 dark:from-orange-800/90 dark:to-orange-900/80"; // Soft orange
      case 4:
        return "from-pink-50/80 to-pink-100/60 dark:from-pink-800/90 dark:to-pink-900/80"; // Soft pink
      case 5:
        return "from-teal-50/80 to-teal-100/60 dark:from-teal-800/90 dark:to-teal-900/80"; // Soft teal
      default:
        return "from-blue-50/80 to-blue-100/60 dark:from-blue-800/90 dark:to-blue-900/80";
    }
  };

  const getBorderColor = () => {
    switch (teamVariation) {
      case 0:
        return "border-blue-200/40 dark:border-blue-600/40";
      case 1:
        return "border-purple-200/40 dark:border-purple-600/40";
      case 2:
        return "border-emerald-200/40 dark:border-emerald-600/40";
      case 3:
        return "border-orange-200/40 dark:border-orange-600/40";
      case 4:
        return "border-pink-200/40 dark:border-pink-600/40";
      case 5:
        return "border-teal-200/40 dark:border-teal-600/40";
      default:
        return "border-blue-200/40 dark:border-blue-600/40";
    }
  };

  return (
    <Card 
      className={cn(
        "relative overflow-hidden cursor-pointer group transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl border",
        `bg-gradient-to-br ${getGradientVariation()}`,
        getBorderColor(),
        isSelected 
          ? "ring-2 ring-primary/50 dark:ring-primary/60 scale-[1.01] animate-scale-in shadow-md" 
          : "hover:shadow-md"
      )}
      onClick={() => onClick(team.id)}
      style={{
        minHeight: '70px',
      }}
    >
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full -translate-y-4 translate-x-4"></div>
        <div className="absolute bottom-0 left-0 w-6 h-6 bg-white rounded-full translate-y-3 -translate-x-3"></div>
      </div>
      
      {/* Subtle shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
      
      <CardContent className="relative p-2.5 flex flex-col gap-1.5 h-full">
        <div className="flex items-center justify-between">
          <div className={cn(
            "flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-all duration-300 group-hover:scale-110 overflow-hidden",
            isSelected ? "bg-background/70 dark:bg-background/80 text-foreground shadow-sm" : "bg-background/50 dark:bg-background/60 text-muted-foreground border border-border"
          )}>
            {team.image_url ? (
              <img 
                src={team.image_url} 
                alt={`${team.name} logo`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <Shield className={`h-3 w-3 ${team.image_url ? 'hidden' : ''}`} />
          </div>
          <span className={cn(
            "text-xs px-1.5 py-0.5 rounded-full font-medium transition-all duration-300",
            isSelected ? "bg-background/60 dark:bg-background/70 text-foreground shadow-sm" : "bg-background/40 dark:bg-background/50 text-muted-foreground border border-border"
          )}>
            Team
          </span>
        </div>
        
        <div className="flex-1 space-y-1.5">
          <h3 className="font-semibold text-sm text-foreground leading-tight group-hover:text-primary transition-colors line-clamp-1">
            {team.name}
          </h3>
          
          <div className="flex items-center justify-between text-foreground/90">
            <div className="flex items-center gap-1">
              <div className={cn(
                "w-5 h-5 rounded-md flex items-center justify-center transition-all duration-300",
                isSelected ? "bg-background/40 dark:bg-background/50" : "bg-background/30 dark:bg-background/40"
              )}>
                <Users className="h-2.5 w-2.5" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-xs text-foreground">{team.guardsCount || 0}</span>
                <span className="text-2xs text-muted-foreground">Guards</span>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <div className={cn(
                "w-5 h-5 rounded-md flex items-center justify-center transition-all duration-300",
                isSelected ? "bg-background/40 dark:bg-background/50" : "bg-background/30 dark:bg-background/40"
              )}>
                <Calendar className="h-2.5 w-2.5" />
              </div>
              <span className="text-2xs font-medium text-foreground">Schedule</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamStatCard;
